<template>

	<transition appear name="fade" @after-enter="showContent = true" @after-leave="afterDialogCloseAnimation">
		<div v-show="showBackdrop" id="backdrop" class="fixed inset-0 bg-black z-50 bg-opacity-80">

			<transition appear name="zoom" @after-leave="showBackdrop = false">
				<div v-show="showContent" class="fixed bottom-0 left-0 right-0 inset-0 grid place-items-center z-40">


					<div class="absolute top-0 left-0 w-full h-full">
						<div class="pt-4 flex h-full">

							<!-- "back" button -->
							<div class="pl-4 absolute">
								<button type="button" @click="handleGoBack" class="touch-btn back-btn">
									<ChevronLeftIcon class="w-4/5 h-4/5" />
								</button>
							</div>

							<div class="h-full flex-grow flex flex-col items-center justify-center text-gray-900 pt-8">

								<div class="relative flex flex-col items-center max-w-64">
									<ActivityBoardAvatar ref="avatar" :pupil="pupil" :withSymbol="activityBoardStore.avatarWithSymbol" :withName="activityBoardStore.avatarWithName" class="h-12 md:h-16 lg:h-20 xl:h-24 mb-4 shadow-lg" />
									<ActivitySymbol v-if="checkedInLessonItem" :activity="checkedInLessonItem" class="mb-4 lg:mb-8 bg-gray-800 border-8 border-gray-900 rounded-lg h-24 md:h-28 lg:h-36 xl:h-44" />
								</div>

								<div class="flex justify-center gap-6">
									<button type="button" v-for="(button, index) in feedbackButtons" :key="button.option" @click="registerFeedback(button.score)" :id="'btn-pop-in-' + (index + 1)" class="touch-btn fb-btn" :class="[button.colorClass, showContent ? 'btn-pop-in' : '']">
										<!-- inverted because an <img>-loaded SVG can't inherit currentColor, so it paints black -->
										<img :src="button.icon" class="w-4/5 h-4/5 invert" alt="" />
									</button>
								</div>
							</div>
						</div>
					</div>


				</div>
			</transition>

		</div>
	</transition>

</template>


<style scoped>
.btn-pop-in {
	animation: 250ms ease-out pop-in;
}

#btn-pop-in-1 {
	animation-delay: 100ms;
}

#btn-pop-in-2 {
	animation-delay: 150ms;
}

#btn-pop-in-3 {
	animation-delay: 200ms;
}

@keyframes pop-in {
	0% {
		transform: scale(1);
	}

	50% {
		transform: scale(1.2)
	}

	100% {
		transform: scale(1);
	}
}

.fb-btn {
	@apply w-24 h-24 md:w-32 md:h-32 lg:w-40 lg:h-40 xl:w-48 xl:h-48 flex justify-center items-center text-white rounded-full
}
</style>


<script lang="ts">
import { useActivityBoardStore, type PupilFeedbackOption } from "@/stores/ActivityBoard.store";
import { mapStores } from "pinia";

import { Pupil } from '@/models/Pupil.model';
import ActivityBoardAvatar from './ActivityBoardAvatar.vue';
import ActivitySymbol from './ui/symbols/ActivitySymbol.vue';
import { ChevronLeftIcon } from '@heroicons/vue/24/outline';
import { PupilActivity } from "@/models/PupilActivity.model";

const FEEDBACK_BUTTONS: { option: PupilFeedbackOption, score: number, colorClass: string, icon: string }[] = [
	{
		option: 1,
		score: 1,
		colorClass: 'bg-yellow-600',
		icon: '/images/icons/Ph-happy.svg',
	},
	{
		option: 0,
		score: 0,
		colorClass: 'bg-yellow-700',
		icon: '/images/icons/Ph-neutral.svg',
	},
	{
		option: -1,
		score: -1,
		colorClass: 'bg-yellow-800',
		icon: '/images/icons/Ph-sad.svg',
	},
];

export default {
	emits: ['requestCheckOut', 'requestClose', 'conflict'],

	props: {
		pupilActivity: {
			type: PupilActivity,
			required: true
		},
	},

	components: {
		ActivityBoardAvatar,
		ActivitySymbol,
		ChevronLeftIcon,
	},

	data() {
		return {
			showContent: false,
			showBackdrop: true,
			score: null,
			// fixed when the dialog opens: the check-in is removed from the board while the dialog is still closing
			checkedInLessonItem: null,
		}
	},

	created() {
		this.checkedInLessonItem = this.activityBoardStore.getLessonItemForCheckedInPupil(this.pupil);
	},

	computed: {
		...mapStores(useActivityBoardStore),

		feedbackButtons() {
			return FEEDBACK_BUTTONS.filter(button =>
				this.activityBoardStore.pupilFeedbackOptions.includes(button.option)
			);
		},

		pupil(): Pupil {
			return this.pupilActivity.pupil;
		},
	},

	methods: {
		registerFeedback(value) {
			// make sure the feedback screen accepts only one feedback tap
			if (this.score !== null) {
				return;
			}
			this.score = value;

			// check out right away: the avatar flies from this dialog back to the pupil list while the dialog closes
			this.$emit('requestCheckOut', this.pupilActivity, value, this.$refs.avatar.$el);

			setTimeout(() => {	// short delay before closing the dialog
				this.showContent = false;
			}, 200);
		},

		afterDialogCloseAnimation() {
			this.$emit('requestClose');
		},

		handleGoBack() {
			this.showContent = false;
		}
	}
}
</script>
